import React, { useState, useRef, useCallback } from 'react';
import FormDataJson from 'form-data-json-convert';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Warning from '../Warning';
import ProductWarningText from './ProductWarningText';

import { mutateImageUrl } from '../../../wordpress';
import { deriveDefaultVariant, computeTorsoOverlayImages } from '../../../utils/customiseHelpers';
import { checkIncompatibilities, formatIncompatibilityAlert } from '../../../utils/incompatibility';
import { isCreateYourOwnHeadProductId } from '../../../constants';

/**
 * Product component
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */
const Product = ({
    product,
    section,
    selectedProducts = {},
    isSelected = false,
    onSelect = () => {},
    onSelectNamedProduct = () => {},
    onRemove = () => {},
    onCustomise = () => {},
    projectTitle = '',
    boldProductOptions = null,
    currentBuildIndex = 0,
    skintone = '',
    prefetchProductDetails = null,
    isSearchResults = false,
    isNoSelectionTile = false,
}) => {
    const [showRemoveAllWarning, setShowRemoveAllWarning] = useState(false);
    const [showRemoveSingleWarning, setShowRemoveSingleWarning] = useState(false);
    const computeInStock = (p) => {
        if (typeof p?.totalInventory === 'number') {
            return p.totalInventory > 0;
        }
        const variantNodes = p?.variants?.nodes || (p?.variants?.edges ? p.variants.edges.map((e) => e.node) : []);
        if (variantNodes.length) {
            const summed = variantNodes.reduce(
                (acc, v) => acc + (typeof v?.quantityAvailable === 'number' ? v.quantityAvailable : 0),
                0
            );
            if (summed > 0) return true;
            // As a secondary signal, if any variant is flagged availableForSale
            if (variantNodes.some((v) => v?.availableForSale)) return true;
        }
        // Fall back to product.availableForSale when no inventory counts provided
        if (typeof p?.availableForSale === 'boolean') return p.availableForSale;
        // Default optimistic (will still be gated by add-to-cart logic elsewhere)
        return true;
    };
    const isInStock = computeInStock(product);
    const prodIdParsed = product && product.id.split('/').pop();
    const classes = classNames('product', {
        'product-selected': isSelected,
        'product-sale': product.on_sale,
    });
    const imgSrc = mutateImageUrl(product.featuredImage && product.featuredImage.url, 480, 480);

    let torsoOverlayImage = null;
    let handImage = null;
    if (section === 'bodies' && product?.images?.edges && skintone) {
        const { attr } = deriveDefaultVariant({ product, skintone, activeSection: 'bodies' });
        const {
            handImage: hImg,
            torsoOverlayImage: tImg,
            isExactMatch,
        } = computeTorsoOverlayImages(product, attr, skintone);
        handImage = hImg || null;
        torsoOverlayImage = (isExactMatch && tImg) || null;

        if (!torsoOverlayImage) {
            const lowerSkin = skintone.toLowerCase();
            let candidate = '';
            let defaultCandidate = '';
            product.images.edges.forEach((edge) => {
                const url = edge?.node?.url || '';
                const lower = url.toLowerCase();
                if (!candidate && lower.includes(`-artwork---${lowerSkin}`)) {
                    candidate = url;
                } else if (!defaultCandidate && lower.includes('-artwork---default')) {
                    defaultCandidate = url;
                }
            });
            torsoOverlayImage = candidate || defaultCandidate || null;
        }
    }

    console.log({ product });

    const hasMultipleVariants = product.variants?.nodes?.length > 1 || product.variants?.edges?.length > 1;
    const isCustomisable =
        (boldProductOptions && boldProductOptions[1]?.option_sets?.length > 0) ||
        isCreateYourOwnHeadProductId(product?.id);
    const hasShopifyOptionDepth = Array.isArray(product?.options) && product.options.length > 1;
    const isAccessorySection = section === 'accessories' && !isSearchResults;

    const [pendingAction, setPendingAction] = useState(null);
    const [compatWarning, setCompatWarning] = useState(null);
    const [showReplaceItemWarning, setShowReplaceItemWarning] = useState(false);
    const deferredReplaceActionRef = useRef(null);

    const handleConfirmCompat = () => {
        if (pendingAction) {
            const action = pendingAction;
            setPendingAction(null);
            setCompatWarning(null);
            action();
        }
    };

    const handleDenyCompat = () => {
        setPendingAction(null);
        setCompatWarning(null);
    };

    const handleNoSelectionClick = () => {
        if (section === 'accessories') {
            setShowRemoveAllWarning(true);
        } else {
            const existingSelection = selectedProducts?.[section];
            if (existingSelection && existingSelection.product) {
                setShowRemoveSingleWarning(true);
            }
        }
    };

    const confirmRemoveAllAccessories = () => {
        const accessories = Array.isArray(selectedProducts?.accessories) ? selectedProducts.accessories : [];
        for (let i = accessories.length - 1; i >= 0; i--) {
            onRemove(`accessories:${i}`);
        }
        setShowRemoveAllWarning(false);
    };

    const confirmRemoveSingle = () => {
        // Remove the single selected item
        onRemove(section);
        setShowRemoveSingleWarning(false);
    };

    const performSelection = (productFormJsonValues) => {
        if (isAccessorySection) {
            onSelectNamedProduct(
                currentBuildIndex,
                product,
                '',
                '',
                product.variants && (product.variants.nodes?.[0] || product.variants.edges?.[0]?.node),
                'accessories',
                productFormJsonValues
            );
            return;
        }

        const productVariant =
            product.variants && product.variants.nodes
                ? product.variants.nodes[0]
                : product.variants && product.variants.edges[0]?.node;

        // For bodies, derive the proper attributes including skintone-aware variant
        // This ensures Preview can compute overlay images correctly
        let attributes = '';
        if (section === 'bodies') {
            const { attr } = deriveDefaultVariant({ product, skintone, activeSection: 'bodies' });
            attributes = attr;
        }

        onSelect(product, attributes, '', productVariant, productFormJsonValues);
    };

    const selectProduct = (e) => {
        const productFormJsonValues = FormDataJson.toJson(e.target.form);

        if (section !== 'accessories') {
            const existing = selectedProducts?.[section];
            const existingId = existing?.product?.id;
            const currentId = product?.id;
            if (existingId && existingId !== currentId) {
                deferredReplaceActionRef.current = () => {
                    performSelection(productFormJsonValues);
                };
                setShowReplaceItemWarning(true);
                return;
            }
        }

        try {
            const incompat = checkIncompatibilities({
                currentBuild: { selected: selectedProducts },
                candidateProduct: product,
                candidateSection: section,
            });
            if (incompat) {
                const msg = formatIncompatibilityAlert(incompat);
                if (msg) {
                    setCompatWarning(msg);
                    // Defer actual selection until user confirms
                    setPendingAction(() => () => performSelection(productFormJsonValues));
                    return;
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Incompatibility check failed', err);
        }

        performSelection(productFormJsonValues);
    };

    const renderRemoveButton = (/*removeSection*/) => {
        // return (
        //     <button type="button" className="btn button span inverse" onClick={() => onRemove(removeSection)}>
        //         <span>Remove</span>
        //     </button>
        // );
        return null;
    };

    const accessorySectionSelected = () => {
        if (!isAccessorySection) return null;
        const accessories = Array.isArray(selectedProducts.accessories) ? selectedProducts.accessories : [];
        const idx = accessories.findIndex((acc) => acc?.product?.id === product.id);
        return idx > -1 ? `accessories:${idx}` : null;
    };

    const hasPrefetchedRef = useRef(false);
    const prefetchTimerRef = useRef(null);
    const triggerPrefetch = useCallback(() => {
        if (hasPrefetchedRef.current) return;
        if (typeof prefetchProductDetails === 'function') {
            hasPrefetchedRef.current = true;
            prefetchProductDetails(product);
        }
    }, [prefetchProductDetails, product]);

    const schedulePrefetch = () => {
        if (hasPrefetchedRef.current) return;
        if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
        prefetchTimerRef.current = setTimeout(triggerPrefetch, 120);
    };

    const cancelPrefetch = () => {
        if (prefetchTimerRef.current) {
            clearTimeout(prefetchTimerRef.current);
            prefetchTimerRef.current = null;
        }
    };

    const getActionButton = () => {
        if (isNoSelectionTile) {
            return null;
        }

        if (isSelected && !isAccessorySection) return renderRemoveButton(section);

        const accSelected = accessorySectionSelected();
        if (accSelected) return renderRemoveButton(accSelected);

        // Show customise button when ANY customisation signal present
        if (isCustomisable || hasMultipleVariants || hasShopifyOptionDepth) {
            return (
                <button type="button" className="button-br button-customise-icons" onClick={() => onCustomise(product)}>
                    <span className="vh">Customise</span>
                </button>
            );
        }

        return (
            <button type="button" className="add-to-build btn" onClick={(e) => selectProduct(e)}>
                <span>Add</span>
            </button>
        );
    };

    const button = getActionButton();

    const isSoldOut = !product.availableForSale || !isInStock;

    if (isSoldOut) return null;

    return (
        <li
            className={`${classes} ${isNoSelectionTile ? 'product-no-selection' : ''}`}
            key={product.id}
            onPointerEnter={isNoSelectionTile ? undefined : schedulePrefetch}
            onPointerLeave={isNoSelectionTile ? undefined : cancelPrefetch}
            onFocus={isNoSelectionTile ? undefined : schedulePrefetch}
        >
            {isNoSelectionTile ? (
                <button
                    type="button"
                    className="product-no-selection-content"
                    onClick={handleNoSelectionClick}
                    disabled={isSelected}
                >
                    <div className="product-no-selection-icon">
                        <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                        </svg>
                    </div>
                    <span className="product-title product-no-selection-title">{product.title}</span>
                    <span className="button-no-selection-text">
                        {section === 'accessories' ? 'Clear All' : 'Select None'}
                    </span>
                </button>
            ) : (
                <>
                    <button
                        type="button"
                        className="product-customise product-standard"
                        onClick={() => onCustomise(product)}
                    >
                        <span className="product-image-stack">
                            <img src={imgSrc} width="480" height="480" alt="" loading="lazy" />
                            {section === 'bodies' && (handImage || torsoOverlayImage) && (
                                <>
                                    {handImage && (!isSearchResults || torsoOverlayImage) && (
                                        <img
                                            src={handImage}
                                            width="480"
                                            height="480"
                                            alt=""
                                            className="product-image-overlay handImage"
                                            loading="lazy"
                                        />
                                    )}

                                    {torsoOverlayImage && (
                                        <img
                                            src={torsoOverlayImage}
                                            width="480"
                                            height="480"
                                            alt=""
                                            className="product-image-overlay torsoOverlayImage"
                                            loading="lazy"
                                        />
                                    )}
                                </>
                            )}
                        </span>
                    </button>

                    <div className="button-icons">
                        {isCustomisable && (
                            <img
                                src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-customise.svg?v=1756990022"
                                alt="Customise"
                                width="20"
                                height="20"
                            />
                        )}

                        {hasMultipleVariants && (
                            <img
                                src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-palette_2cd45be0-5921-4428-8d88-25a37f5e2335.svg?v=1759396488"
                                alt="Customise"
                                width="20"
                                height="20"
                            />
                        )}

                        <ProductWarningText product={product} />
                    </div>

                    <span className="product-title">
                        <form
                            action="/cart/add"
                            method="post"
                            encType="multipart/form-data"
                            id={`AddToCartForm-${prodIdParsed}`}
                        >
                            <input type="number" name="quantity" value="1" hidden readOnly />
                            <input
                                name="id"
                                value={product.variants?.nodes ? product.variants?.nodes[0]?.id?.split('/').pop() : ''}
                                hidden
                                readOnly
                            />
                            <input
                                type="text"
                                id={`projectTitle-${
                                    product.variants?.nodes ? product.variants?.nodes[0]?.id?.split('/').pop() : ''
                                }`}
                                name="properties[projectTitle]"
                                value={projectTitle ? projectTitle : ''}
                                hidden
                                readOnly
                            />
                        </form>
                    </span>

                    {compatWarning && (
                        <Warning
                            title={compatWarning.title}
                            warningText={`${compatWarning.message}\n\nProceed anyway?`}
                            actionConfirm={handleConfirmCompat}
                            actionDeny={handleDenyCompat}
                        />
                    )}

                    {showReplaceItemWarning && (
                        <Warning
                            warningText={'Are you sure you want to make this change?'}
                            actionConfirm={() => {
                                const fn = deferredReplaceActionRef.current;
                                setShowReplaceItemWarning(false);
                                deferredReplaceActionRef.current = null;
                                if (fn) fn();
                            }}
                            actionDeny={() => {
                                setShowReplaceItemWarning(false);
                                deferredReplaceActionRef.current = null;
                            }}
                        />
                    )}
                </>
            )}

            {showRemoveAllWarning && (
                <Warning
                    title="Remove All Accessories?"
                    warningText="This will remove all accessories from this build. This action cannot be undone."
                    confirmLabel="Remove All"
                    cancelLabel="Cancel"
                    actionConfirm={confirmRemoveAllAccessories}
                    actionDeny={() => setShowRemoveAllWarning(false)}
                />
            )}

            {showRemoveSingleWarning && (
                <Warning
                    title="Remove Selection?"
                    warningText="Are you sure you want to remove this item from your build?"
                    confirmLabel="Remove"
                    cancelLabel="Cancel"
                    actionConfirm={confirmRemoveSingle}
                    actionDeny={() => setShowRemoveSingleWarning(false)}
                />
            )}

            {isSoldOut ? (
                <p className="btn button span disabled soldout-2">
                    <span>Sold out</span>
                </p>
            ) : (
                button
            )}
        </li>
    );
};

Product.propTypes = {
    product: PropTypes.object.isRequired,
    section: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    onRemove: PropTypes.func,
    onCustomise: PropTypes.func,
    selectedProducts: PropTypes.object,
    onSelectNamedProduct: PropTypes.func,
    projectTitle: PropTypes.string,
    boldProductOptions: PropTypes.any,
    currentBuildIndex: PropTypes.number,
    skintone: PropTypes.string,
    prefetchProductDetails: PropTypes.func,
    isSearchResults: PropTypes.bool,
    isNoSelectionTile: PropTypes.bool,
};

export default Product;
